import { Loader2 } from "lucide-react";

import { useProposalDiff } from "@/lib/api";
import { cn } from "@/lib/utils";

interface DiffViewProps {
  proposalId: string;
}

function lineClass(line: string) {
  if (line.startsWith("@@")) {
    return "bg-accent-light/40 text-muted";
  }
  if (line.startsWith("+")) {
    return "bg-success/15";
  }
  if (line.startsWith("-")) {
    return "bg-destructive/15";
  }
  return "";
}

export function DiffView({ proposalId }: DiffViewProps) {
  const { data, isLoading, isError, refetch } = useProposalDiff(proposalId, true);

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="text-muted h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <p className="text-muted py-3 text-sm">
        Could not load the diff.{" "}
        <button type="button" className="underline" onClick={() => void refetch()}>
          Try again
        </button>
      </p>
    );
  }

  if (data.files.length === 0) {
    return <p className="text-muted py-3 text-sm">This proposal has no file changes.</p>;
  }

  return (
    <div className="space-y-3">
      {data.files.map((file) => (
        <div key={file.path} className="border-border overflow-hidden rounded-md border">
          <div className="border-border bg-background flex flex-wrap items-center justify-between gap-2 border-b px-3 py-2 text-xs">
            <span className="font-mono break-all">{file.path}</span>
            <span className="text-muted">
              {file.status} · <span className="text-success">+{file.additions}</span>{" "}
              <span className="text-destructive">-{file.deletions}</span>
            </span>
          </div>
          {file.patch ? (
            <pre className="bg-card overflow-x-auto py-1 font-mono text-xs leading-5">
              {file.patch.split("\n").map((line, i) => (
                <div key={i} className={cn("px-3 whitespace-pre", lineClass(line))}>
                  {line || " "}
                </div>
              ))}
            </pre>
          ) : (
            <p className="text-muted px-3 py-2 text-xs">No text diff available.</p>
          )}
        </div>
      ))}
    </div>
  );
}
